import { ViewType } from '../../assets/constants'
import FnoPageWrapper from '../fnoPageWrapper'
import HomeLayout from '../homeLayout'
import StrategyBuilderTabView from '../../components/strategyBuilder/strategyBuilderTabView'
import { useStyles } from './styles'
import StrategyBuilderSubHeader from '../../components/strategyBuilder/subHeader'
import { memo, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Box } from '@mui/material'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import {
    resetStrategyBuilder,
    setBatchId,
    setBasketId,
    setStrategyBuilderMode,
    setStrategyGroup,
    updateNearestExpiry,
    updateTradeOrders
} from '../../reducer/strategyBuilder.reducer'
import StrategyBuilderNearestExpiryEffects from './StrategyBuilderNearestExpiryEffects'
import OptionChainRightDrawer from '../home/optionChainViewTabs/OptionChainRightDrawer'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { exchangeFromJSON } from '../../proto/generated/common'
import { getSecurityKey } from '../../util/security'
import {
    STRATEGY_BUILDER_LOCATION_PENDING_KEY,
    type StrategyBuilderLocationState
} from '../../util/strategyBuilderFromPositionsNavigation'
import commonActions from '../../actions/common.actions'
import { WidgetListItems } from '../../util/sidebar'

export type StrategyBuilderProps = {
    simulator: boolean
}

const StrategyBuilder: React.FC<StrategyBuilderProps> = ({ simulator = false }) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const params = useParams()
    const { primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity()
    const previousUnderlyingToken = useRef<string>(null)

    useEffect(() => {
        return () => {
            dispatch(resetStrategyBuilder())
        }
    }, [])

    useEffect(() => {
        if(underlyingToken) {
            const prevToken = previousUnderlyingToken.current
            if (prevToken && underlyingToken !== prevToken) {
                dispatch(resetStrategyBuilder())
            }
            previousUnderlyingToken.current = underlyingToken
        }
    }, [underlyingToken])

    useEffect(() => {
        if (simulator) return
        const pending = (location.state as StrategyBuilderLocationState | null)?.[STRATEGY_BUILDER_LOCATION_PENDING_KEY]
        if (!pending) return
        const { exchange: urlExchange, symbol: urlCode } = params
        if (!urlExchange || !urlCode) return
        const expectedKey = getSecurityKey(exchangeFromJSON(urlExchange), urlCode)
        if (!underlyingToken || underlyingToken !== expectedKey) return

        if (pending.kind === 'analyze') {
            dispatch(setBatchId(pending.batchId))
            dispatch(setBasketId(pending.basketId))
            dispatch(setStrategyBuilderMode('analyze'))
            dispatch(setStrategyGroup(pending.strategyGroup))
            dispatch(updateTradeOrders([]))
            commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
        } else {
            dispatch(setBatchId(pending.batchId))
            dispatch(setBasketId(pending.basketId))
            dispatch(setStrategyBuilderMode('squareOff'))
            dispatch(setStrategyGroup(pending.strategyGroup))
            dispatch(updateTradeOrders(pending.orders))
            commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
        }

        const prevState = (location.state as Record<string, unknown> | null) ?? {}
        const nextState = { ...prevState }
        delete nextState[STRATEGY_BUILDER_LOCATION_PENDING_KEY]
        navigate(
            { pathname: location.pathname, search: location.search, hash: location.hash },
            { replace: true, state: Object.keys(nextState).length ? nextState : null }
        )
    }, [simulator, location.state, location.pathname, location.search, location.hash, params.exchange, params.symbol, underlyingToken, dispatch, navigate])

    return (
        <>
            <StrategyBuilderNearestExpiryEffects />
            <HomeLayout view={simulator ? ViewType.SIMULATOR : ViewType.STRATEGY_BUILDER}>
                <FnoPageWrapper simulator={simulator}>
                    {
                        <div className={classes.container}>
                            <div style={{ gridArea: 'header' }}>
                                <StrategyBuilderSubHeader simulator={simulator} />
                            </div>
                            <Box sx={{ gridArea: 'content', display: 'flex', flexDirection: 'column' }}>
                                <StrategyBuilderTabView simulator={simulator} />
                            </Box>
                        </div>
                    }
                </FnoPageWrapper>
            </HomeLayout>
            <OptionChainRightDrawer />
        </>
    )
}

export default memo(StrategyBuilder)
